import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { systemApi, versionsApi } from '../api';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

/**
 * The active checkpoint version, and the worker/GPU status that goes with it.
 *
 * Lives at app level because three places need it at once: the version pill in
 * the top bar, every inference request, and the "is this thing even running"
 * status. Selecting a version is an explicit user action — nothing auto-loads a
 * version behind the user's back, since loading one costs GPU time and changes
 * what every subsequent answer means.
 *
 * Two serving backends, chosen on the server (INFERENCE_BACKEND):
 *
 * - **hf** — one worker, one loaded version, shared by everyone. Selecting a
 *   version loads it on the worker, and the worker says which one is active.
 * - **vllm** — base models are loaded by an administrator (Settings → Model
 *   serving) and every request names its own version, so each person picks
 *   theirs independently. Picking one whose base model is already on the GPU
 *   costs nothing; an administrator picking one that is not starts loading it.
 */

const ModelContext = createContext(null);

const STATUS_POLL_MS = 15000;
const LOADING_POLL_MS = 5000;
const SELECTION_KEY = 'irap.vlm.selectedVersion';

function readSelection() {
  try {
    return window.localStorage.getItem(SELECTION_KEY) || null;
  } catch {
    return null;
  }
}

function writeSelection(versionId) {
  try {
    if (versionId) window.localStorage.setItem(SELECTION_KEY, versionId);
    else window.localStorage.removeItem(SELECTION_KEY);
  } catch {
    /* private mode or blocked storage: the selection just does not persist */
  }
}

export function ModelProvider({ children }) {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [versions, setVersions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  const backend = status?.inference_backend || 'hf';
  const isVllm = backend === 'vllm';

  const refreshVersions = useCallback(async () => {
    try {
      const list = await versionsApi.list();
      setVersions(list);
      const active = list.find((version) => version.is_active);
      setActiveId((current) => active?.id ?? current);
      setError(null);
      return list;
    } catch (cause) {
      setError(cause);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const payload = await systemApi.status();
      setStatus(payload);
      // Only the HF worker has a single active version. Under vLLM the worker
      // runs no language model, and the selection is each person's own.
      const workerActive = payload?.inference_backend !== 'vllm' && payload?.worker?.active_version;
      if (workerActive) setActiveId(workerActive);
      return payload;
    } catch {
      setStatus(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshVersions();
    refreshStatus();
  }, [refreshVersions, refreshStatus]);

  useEffect(() => {
    const timer = setInterval(refreshStatus, STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [refreshStatus]);

  // vLLM: restore this person's last choice, or fall back to a version whose base
  // model is already serving — selecting that loads nothing.
  useEffect(() => {
    if (!isVllm || !versions.length) return;
    setActiveId((current) => {
      const usable = (id) => versions.some((v) => v.id === id && v.is_ready && !v.is_locked);
      if (current && usable(current)) return current;
      const stored = readSelection();
      if (stored && usable(stored)) return stored;
      const serving = versions.find(
        (v) => v.is_ready && !v.is_locked && v.serving?.state === 'ready',
      );
      return serving?.id ?? null;
    });
  }, [isVllm, versions]);

  // While a base model is loading or draining, watch it more closely.
  const transitioning = useMemo(
    () =>
      isVllm &&
      versions.some((v) => ['starting', 'draining', 'pending'].includes(v.serving?.state)),
    [isVllm, versions],
  );
  useEffect(() => {
    if (!transitioning) return undefined;
    const timer = setInterval(() => {
      refreshVersions();
      refreshStatus();
    }, LOADING_POLL_MS);
    return () => clearInterval(timer);
  }, [transitioning, refreshVersions, refreshStatus]);

  const selectServed = useCallback(
    async (versionId) => {
      const version = versions.find((candidate) => candidate.id === versionId);
      const label = version?.label || versionId;
      const serving = version?.serving || {};
      if (serving.state === 'ready') {
        setActiveId(versionId);
        writeSelection(versionId);
        toast.success('Version selected', `${label} · ${serving.weights_label || 'vLLM'}`);
        return true;
      }
      if (!isAdmin) {
        toast.error(
          {
            code: 'model_not_loaded',
            message:
              serving.state === 'starting'
                ? `${serving.model_source} is still loading. Try again in a minute.`
                : `${label} needs ${serving.model_source || 'its base model'}, which is not loaded. Ask an administrator to load it.`,
          },
          { title: serving.state === 'starting' ? 'Model still loading' : 'Model not loaded' },
        );
        return false;
      }
      setSwitching(true);
      try {
        const result = await versionsApi.load(versionId);
        setActiveId(versionId);
        writeSelection(versionId);
        await Promise.all([refreshVersions(), refreshStatus()]);
        if (result?.already_loaded) {
          toast.success('Version selected', `${label} · ${result.weights_mode}`);
        } else {
          const replaced = result?.stopped?.length
            ? ` Unloaded ${result.stopped.join(', ')} to make room (single-model serving).`
            : '';
          toast.info(
            'Loading base model',
            `${result?.model_source || label} is starting with ${Math.round(
              (result?.gpu_fraction || 0) * 100,
            )}% of the GPU. This takes one to three minutes.${replaced}`,
          );
        }
        return true;
      } catch (cause) {
        toast.error(cause, { title: 'Could not load that version' });
        return false;
      } finally {
        setSwitching(false);
      }
    },
    [isAdmin, refreshStatus, refreshVersions, toast, versions],
  );

  const selectVersion = useCallback(
    async (versionId) => {
      if (!versionId) return true;
      if (isVllm) return selectServed(versionId);
      if (versionId === activeId) return true;
      setSwitching(true);
      try {
        const workerStatus = await versionsApi.load(versionId);
        setActiveId(versionId);
        setStatus((current) => ({ ...(current || {}), worker: workerStatus }));
        await refreshVersions();
        const version = versions.find((candidate) => candidate.id === versionId);
        toast.success(
          'Version loaded',
          `${version?.label || versionId} · ${workerStatus?.adapter_params ?? 0} adapter parameters`,
        );
        return true;
      } catch (cause) {
        toast.error(cause, { title: 'Could not load that version' });
        return false;
      } finally {
        setSwitching(false);
      }
    },
    [activeId, isVllm, refreshVersions, selectServed, toast, versions],
  );

  const unloadModel = useCallback(
    async ({ releaseBase = false, versionId = null } = {}) => {
      setSwitching(true);
      try {
        if (isVllm) {
          // Unloading under vLLM stops a base model for everyone using it, so it
          // names the version whose base model goes.
          const target = versionId || activeId;
          const result = await versionsApi.unload({ versionId: target });
          await Promise.all([refreshVersions(), refreshStatus()]);
          toast.success(
            'Model unloading',
            `${result?.server_key || target}: in-flight requests finish first, then its GPU memory is released.`,
          );
          return true;
        }
        const workerStatus = await versionsApi.unload({ releaseBase });
        setActiveId(null);
        setStatus((current) => ({ ...(current || {}), worker: workerStatus }));
        await refreshVersions();
        // vram_used_gb after a release is the CUDA context, which the driver only
        // frees when the worker process exits. Saying so avoids the obvious question
        // when nvidia-smi does not read zero.
        const used = workerStatus?.vram_used_gb;
        toast.success(
          releaseBase ? 'GPU released' : 'Adapter detached',
          releaseBase
            ? `Base model dropped. ${
                used ? `${used} GB still held by the CUDA context; ` : ''
              }the next request reloads the model.`
            : 'The base model stays resident, so the next load is fast.',
        );
        return true;
      } catch (cause) {
        toast.error(cause, { title: 'Could not unload the model' });
        return false;
      } finally {
        setSwitching(false);
      }
    },
    [activeId, isVllm, refreshStatus, refreshVersions, toast],
  );

  const setVersionLocked = useCallback(
    async (versionId, locked, reason = '') => {
      try {
        if (locked) await versionsApi.lock(versionId, reason);
        else await versionsApi.unlock(versionId);
        await refreshVersions();
        toast.success(locked ? 'Model locked' : 'Model unlocked');
        return true;
      } catch (cause) {
        toast.error(cause, { title: locked ? 'Could not lock' : 'Could not unlock' });
        return false;
      }
    },
    [refreshVersions, toast],
  );

  const patchVersion = useCallback(async (versionId, patch) => {
    const updated = await versionsApi.patch(versionId, patch);
    setVersions((current) =>
      current.map((version) => (version.id === versionId ? { ...version, ...updated } : version)),
    );
    return updated;
  }, []);

  const activeVersion = useMemo(
    () => versions.find((version) => version.id === activeId) || null,
    [versions, activeId],
  );

  // Locked versions are excluded here, not just hidden in Settings: readyVersions
  // feeds the chat page's model picker and the needsSetup prompt, so a locked model
  // must not be selectable there either.
  const readyVersions = useMemo(
    () => versions.filter((version) => version.is_ready && !version.is_locked),
    [versions],
  );

  const workerAlive = Boolean(status?.worker && !status.worker.error);
  const serving = status?.serving || null;
  const modelServerReachable = Boolean(serving?.model_server?.reachable);

  // Whether a generation request can be served at all, and if not, why — the
  // single answer every page's "disable the Run button" logic needs.
  const { inferenceAlive, inferenceProblem } = useMemo(() => {
    if (!isVllm) {
      return {
        inferenceAlive: workerAlive,
        inferenceProblem: workerAlive ? null : 'The inference worker is not reachable.',
      };
    }
    if (!modelServerReachable) {
      return {
        inferenceAlive: false,
        inferenceProblem:
          serving?.model_server?.error || 'The model server (vLLM) is not reachable.',
      };
    }
    const state = activeVersion?.serving?.state;
    if (activeVersion && state && state !== 'ready') {
      return {
        inferenceAlive: false,
        inferenceProblem:
          state === 'starting'
            ? `${activeVersion.serving.model_source} is still loading.`
            : `${activeVersion.serving.model_source || 'The base model'} is not loaded (${state}).`,
      };
    }
    return { inferenceAlive: true, inferenceProblem: null };
  }, [activeVersion, isVllm, modelServerReachable, serving, workerAlive]);

  const value = useMemo(
    () => ({
      versions,
      readyVersions,
      activeVersion,
      activeId,
      status,
      worker: status?.worker || null,
      workerAlive,
      /** 'hf' (one worker, one loaded version) or 'vllm' (many models, many users). */
      backend,
      isVllm,
      serving,
      inferenceAlive,
      inferenceProblem,
      loading,
      switching,
      error,
      refreshVersions,
      refreshStatus,
      selectVersion,
      unloadModel,
      setVersionLocked,
      patchVersion,
      /** True when nothing can run yet — the UI shows a setup prompt instead of a composer. */
      needsSetup: !loading && readyVersions.length === 0,
    }),
    [
      versions,
      readyVersions,
      activeVersion,
      activeId,
      status,
      workerAlive,
      backend,
      isVllm,
      serving,
      inferenceAlive,
      inferenceProblem,
      loading,
      switching,
      error,
      refreshVersions,
      refreshStatus,
      selectVersion,
      unloadModel,
      setVersionLocked,
      patchVersion,
    ],
  );

  return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
}

export function useModel() {
  const context = useContext(ModelContext);
  if (!context) throw new Error('useModel must be used inside ModelProvider');
  return context;
}
